import * as React from 'react'
import PhoneInput from 'react-phone-number-input/input'
import haileabHeadshot from '../../../assets/haileab-headshot.png'
import rideshareGuy from '../../../assets/rideshare-guy.png'
import uberLogo from '../../../assets/uber-logo.png'
import { ErrorMessage } from '../../../shared-components/ErrorMessage'
import { IconButton } from '../../../shared-components/IconButton'
import { useScreenTracking } from '../../../shared-hooks/useScreenTracking'
import { useScrollToTop } from '../../../shared-hooks/useScrollToTop'
import { track } from '../../../utils/analytics'
import { SignupCard } from '../SignupCard'
import { SignupStep } from '../SignupFlow'
import { SSOStepProps } from './SSOFlow'

type UberPhoneProps = SSOStepProps & {
  setSignupStep: (step: SignupStep) => void
  setUserPhoneNumber: React.Dispatch<React.SetStateAction<string>>
}

export const UberPhone = ({
  makeSSORequest,
  setSignupStep,
  setUserPhoneNumber,
}: UberPhoneProps): JSX.Element => {
  useScreenTracking('UberPhoneView', 'View uber phone input screen')

  const [isTOSChecked, setIsTOSChecked] = React.useState(false)
  const [phoneNumber, setPhoneNumber] = React.useState('')

  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState('')

  const inputElement = React.useRef<HTMLInputElement>(null)
  useScrollToTop(inputElement)

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsSubmitting(true)
    setErrorMessage('')

    track('UberSSOAttempt', 'Uber SSO attempt')

    if (phoneNumber.length !== 12) {
      track('UberSSOFailure', 'Uber SSO failure')
      setErrorMessage('Please enter a valid 10 digit phone number.')
      setIsSubmitting(false)
      return
    }

    const body = {
      stage: 'initial',
      credentials: {
        phone_number: phoneNumber,
      },
    }

    try {
      setUserPhoneNumber(phoneNumber)
      await makeSSORequest(body, 'Invalid phone number. Please try again.')
      track('UberSSOSuccess', 'Uber SSO success')
    } catch (error) {
      track('UberSSOFailure', 'Uber SSO failure')
      setIsSubmitting(false)
      setErrorMessage(error?.message)
    }
  }

  return (
    <SignupCard
      headerComponent={<SmallTestimonial />}
      heading='Get started with Uber'
    >
      <form className='flex flex-col gap-4' onSubmit={onSubmit}>
        <label
          htmlFor='uber-phone'
          className='text-sm font-extralight text-body-black ml-1 -mb-1'
        >
          Phone Number Used for Uber
        </label>
        <PhoneInput
          maxLength={14}
          className='appearance-none rounded-lg p-4 border bg-zinc-200 font-light placeholder-neutral-600'
          country='US'
          placeholder='Phone Number'
          id='uber-phone'
          value={phoneNumber}
          onChange={setPhoneNumber}
          ref={inputElement}
          required
        />
        <label className='mt-1 ml-1 flex items-center'>
          <input
            className='mr-2'
            type='checkbox'
            checked={isTOSChecked}
            onChange={(event) => setIsTOSChecked(event.target.checked)}
            disabled={isSubmitting}
            required
          />
          <span className='text-sm font-extralight text-body-black'>
            I agree to rideBelay's{' '}
            <a
              target='_blank'
              rel='noopener noreferrer'
              href='https://www.ridebelay.com/terms-of-service'
              className='underline underline-offset-1'
              onClick={() => track('TOSClick', 'TOS Click')}
            >
              Terms of Service
            </a>
          </span>
        </label>
        <ErrorMessage>{errorMessage}</ErrorMessage>
        <IconButton
          type='submit'
          isSubmitting={isSubmitting}
          iconSrc={uberLogo}
        >
          Continue With Uber
        </IconButton>
        <a
          className='text-sm font-extralight ml-1 mt-2'
          role='button'
          onClick={() => setSignupStep(SignupStep.Waitlist)}
        >
          Click here for UberEats, Lyft, and Doordash
        </a>
        {/* <Button type='submit' isSubmitting={isSubmitting}>
          Continue
        </Button> */}
        {/* <RideShareGuySocialProof /> */}
        {/* <p className='text-xl font-extralight mt-5 ml-1'>Or download the app</p>
        <AppDownloadButtons /> */}
      </form>
    </SignupCard>
  )
}

const SmallTestimonial = (): JSX.Element => (
  <figure className='lg:hidden flex items-center font-extralight mb-12 -mt-3'>
    <img className='w-20 mr-4' src={haileabHeadshot} />
    <div>
      <blockquote className='text-xs leading-loose'>
        "Guaranteeing my income without sacrificing my flexibility is a no
        brainer."
      </blockquote>
      <figcaption className='text-[.675rem] pt-3'>
        <cite>Haileab Mered, Belay early adopter</cite>
      </figcaption>
    </div>
  </figure>
)

const RideShareGuySocialProof = (): JSX.Element => (
  <div className='mt-8'>
    <p className='text-sm font-extralight text-body-black ml-2 inline'>
      As seen on:
    </p>
    <img className='max-w-[66%] ml-4 inline-block' src={rideshareGuy} />
  </div>
)
